import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { Header, Page } from 'redesign';
import { useParams } from 'react-router-dom';
import { invariant } from 'shared/utils/utils';
import { useSiteDetection } from 'shared/hooks';
import { RenderUnsettledUI } from 'shared/components';
import { WorkflowDetailsResponseDto } from 'api/services/workflow/workflow-service.types';
import { useGetWorkflowDetails } from './hooks/use-get-workflow-details';
import { WorkflowEditToolbox } from './workflow-edit-toolbox/workflow-edit-toolbox';
import { useWorkflowGraph } from '../common/workflow-editor/hooks/use-workflow-graph';
import { mapWorkflowDetailsToCanvas } from '../common/workflow-editor/mappers/map-workflow-details-to-canvas';
import { WorkflowEditor } from '../common/workflow-editor/workflow-editor';
import { WorkflowCreateForm } from '../workflow-create/workflow-create-toolbox/workflow-create-toolbox';

type WorkflowEditContentProps = {
  data: WorkflowDetailsResponseDto;
};

export const WorkflowEdit = () => {
  const { id } = useParams<'id'>();
  invariant(id, 'Workflow ID is required');
  const { site } = useSiteDetection();

  const workflowDetailsQuery = useGetWorkflowDetails(site, id);

  if (workflowDetailsQuery.status !== 'success') {
    return <RenderUnsettledUI data={workflowDetailsQuery} />;
  }

  return <WorkflowEditContent data={workflowDetailsQuery.data} />;
};

const WorkflowEditContent = ({ data }: WorkflowEditContentProps) => {
  const { t } = useTranslation(['web-common', 'ogp-screen-specific']);
  const initialGraphState = useMemo(() => mapWorkflowDetailsToCanvas(data.graph), [data.graph]);
  const graphControl = useWorkflowGraph(initialGraphState);
  const form = useForm<WorkflowCreateForm>({
    defaultValues: { workflowName: data.name },
  });

  return (
    <FormProvider {...form}>
      <Page
        header={<Header titleText={t('workflows.edit.title', { ns: 'ogp-screen-specific' })} />}
        toolbox={<WorkflowEditToolbox />}
      >
        <WorkflowEditor graphControl={graphControl} addNodeDisabled />
      </Page>
    </FormProvider>
  );
};
